import { Component, OnInit, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EMPLOYEES_ROUTES } from "../const";
import { combineLatest, Observable } from "rxjs";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { EstadoRegistro, RangoFechasRegistro_DTO, RegistroHoras, RegistroHorasFilter, RegistroDetalleTiempo, RegistroPausa } from "../../../models/personal/registro-horas.model";
import { RegistroHorasService } from "../../../services/employees/registro-horas.service";
import { EmployeesListService } from "../employees-list.service";
import { EmployeesListModel } from "../employees-list.model";
import { DialogsService } from "../../../shared/components/dialogs/dialogs.service";
import { Utils } from "../../../shared/utils";
import { consts } from "../../../consts";
import { TiempoFormComponent } from "./tiempo-form.component";
import { map } from "rxjs/operators";
import { PaginatedResult } from "../../../models/base";
import { MotivoDialogComponent } from "./components/motivo-dialog/motivo-dialog.component";
import { EditarRegistroModalComponent } from "./components/editar-registro-modal/editar-registro-modal.component";
import { HistorialRegistroModalComponent } from "./components/historial-registro-modal/historial-registro-modal.component";
import * as FileSaver from 'file-saver';
import moment from "moment";

interface RegistroTiempoSemanal {
  semana: string;
  totalSemana: string;
  dias: RegistroTiempoDia[];
}

interface RegistroTiempoDia {
  fecha: Date;
  totalDia: string;
  registros: RegistroDetalleTiempo[];
}

@Component({
    selector: "app-tiempo",
    templateUrl: "./tiempo.component.html",
    styleUrls: ["./tiempo.component.css"],
    standalone: false
})
export class TiempoComponent implements OnInit, OnDestroy {
  // #region Propiedades del componente
  displayedColumns: string[] = [
    'cadenaInicio',
    'cadenaFin',
    'estado',
    'finalizadoAutomatico',
    'acciones'
  ];
  empleados: EmployeesListModel[] = [];
  formBusqueda: UntypedFormGroup = null;
  loading$: Observable<boolean>;
  registrosHoras$: Observable<RegistroHoras[]> = this.registroHorasService.registrosHoras$;
  routes: object[] = [];
  row: RegistroHoras = null;
  currentTime: Date = new Date();
  private timeInterval: any;

  // Registros agrupados
  registrosCargados: boolean = false;
  registrosHoy: RegistroDetalleTiempo[] = [];
  registrosSemanaActual: RegistroDetalleTiempo[] = [];
  registrosMesActual: RegistroDetalleTiempo[] = [];
  registrosMesPasado: RegistroDetalleTiempo[] = [];
  registrosRangoFechas: RegistroDetalleTiempo[] = [];

  // Vista agregada de empresa
  mostrarEmpresa: boolean = false;
  formRangoEmpresa: UntypedFormGroup = null;
  registrosEmpresa: RegistroDetalleTiempo[] = [];
  registrosEmpresaCargados: boolean = false;
  private static readonly TAMANO_PAGINA_EMPRESA = 300;
  // #endregion

  constructor(
    private router: Router,
    public dialog: MatDialog,
    public dialogsService: DialogsService,
    public employeesListService: EmployeesListService,
    public registroHorasService: RegistroHorasService,
    protected snackBar: MatSnackBar
  ) {
    this.routes = EMPLOYEES_ROUTES;

    this.loading$ = combineLatest([
      this.employeesListService.loading$,
      this.registroHorasService.loading$
    ]).pipe(
      map(([loading1, loading2]) => loading1 || loading2)
    );

    this.timeInterval = setInterval(() => {
      this.currentTime = new Date();
    }, 1000);
  }

  // #region Métodos del componente

  ngOnInit() {
    // Obtener la lista de empleados
    this.employeesListService.getList().then((data) => {
      this.empleados = data;
      this.inicializarFormulario();
    });

    this.formRangoEmpresa = new UntypedFormGroup({
      fechaInicio: new UntypedFormControl(null, [Validators.required]),
      fechaFin: new UntypedFormControl(null, [Validators.required]),
    });
  }

  ngOnDestroy() {
    if (this.timeInterval) {
      clearInterval(this.timeInterval);
    }
  }

  actualizarRegistros(event): void {
    this.cargarRegistros();
  }

  cancelar(): void {
    if (this.formBusqueda != null) {

      this.formBusqueda.reset();
    }

    this.registroHorasService.limpiarListaRegistroPorEmpleado();
    this.registrosHoy = [];
    this.registrosSemanaActual = [];
    this.registrosMesActual = [];
    this.registrosMesPasado = [];
    this.registrosCargados = false;
  }

  cargarRegistros(): void {
    this.registroHorasService.limpiarListaRegistroPorEmpleado();
    this.registrosHoy = [];
    this.registrosSemanaActual = [];
    this.registrosMesActual = [];
    this.registrosMesPasado = [];
    this.registrosCargados = false;

    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    // Calcular todas las fechas necesarias
    const inicioMesPasado = this.getInicioMesPasado(hoy);
    const inicioSemana = this.getInicioSemana(hoy);
    const inicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);

    // Hacer una única llamada desde el inicio del mes pasado hasta hoy
    const filtro: RegistroHorasFilter = {
      employeesId: this.formBusqueda.get('empleado').value,
      fechaInicio: inicioMesPasado,
      fechaFin: new Date(),
      pageSize: 500 // Aumentamos el tamaño para asegurar que obtenemos todos los registros
    };

    this.registroHorasService.getRegistrosHoras(filtro).subscribe({
      next: (response: PaginatedResult<RegistroDetalleTiempo>) => {
        if (response && response.items) {
          const registros = response.items;

          this.registrosCargados = true;

          // Distribuir los registros en las diferentes variables según su fecha
          this.registrosHoy = registros.filter(registro => {
            const fechaRegistro = new Date(registro.fecha);
            return this.esMismaFecha(fechaRegistro, hoy);
          });

          this.registrosSemanaActual = registros.filter(registro => {
            const fechaRegistro = new Date(registro.fecha);
            return fechaRegistro >= inicioSemana && fechaRegistro <= new Date();
          });

          this.registrosMesActual = registros.filter(registro => {
            const fechaRegistro = new Date(registro.fecha);
            return fechaRegistro >= inicioMes && fechaRegistro <= new Date();
          });

          this.registrosMesPasado = registros.filter(registro => {
            const fechaRegistro = new Date(registro.fecha);
            const finMesPasado = new Date(inicioMes.getTime() - 1);
            return fechaRegistro >= inicioMesPasado &&
              fechaRegistro <= finMesPasado;
          });

          // En el caso de que no haya registros, se debe mostrar un mensaje de error
          if (this.registrosHoy.length === 0 && this.registrosSemanaActual.length === 0 && this.registrosMesActual.length === 0 && this.registrosMesPasado.length === 0) {
            this.snackBar.open('No se encontraron registros para los dos últimos meses del empleado seleccionado.', 'Cerrar', {
              duration: 6000,
              panelClass: ['mat-toolbar', 'mat-warn']
            });
          }
        }
      },
      error: (error) => {
        console.error('Error al cargar los registros:', error);
        this.snackBar.open('Error al cargar los registros', 'Cerrar', {
          duration: 3000
        });
      }
    });
  }

  private calcularTotalTiempo(registros: RegistroDetalleTiempo[]): string {
    let totalMinutos = 0;

    registros.forEach(registro => {
      if (registro.inicio && registro.fin) {
        const inicio = new Date(registro.inicio);
        const fin = new Date(registro.fin);
        const diferencia = fin.getTime() - inicio.getTime();
        totalMinutos += diferencia / (1000 * 60);
      }
    });

    const horas = Math.floor(totalMinutos / 60);
    const minutos = Math.floor(totalMinutos % 60);
    return `${horas.toString().padStart(2, '0')}:${minutos.toString().padStart(2, '0')}`;
  }

  private getInicioSemana(fecha: Date): Date {
    const inicio = new Date(fecha);
    const diaSemana = inicio.getDay();
    const diff = inicio.getDate() - diaSemana + (diaSemana === 0 ? -6 : 1); // Ajustar cuando es domingo
    inicio.setDate(diff);
    inicio.setHours(0, 0, 0, 0);
    return inicio;
  }

  private getInicioMesPasado(fecha: Date): Date {
    const inicio = new Date(fecha);
    if (inicio.getMonth() === 0) {
      // Si estamos en enero, el mes pasado es diciembre del año anterior
      inicio.setFullYear(inicio.getFullYear() - 1);
      inicio.setMonth(11);
    } else {
      inicio.setMonth(inicio.getMonth() - 1);
    }
    inicio.setDate(1);
    inicio.setHours(0, 0, 0, 0);
    return inicio;
  }

  private esMismaFecha(fecha1: Date, fecha2: Date): boolean {
    return fecha1.getFullYear() === fecha2.getFullYear() &&
      fecha1.getMonth() === fecha2.getMonth() &&
      fecha1.getDate() === fecha2.getDate();
  }

  nombreEmpleado(idEmpleado: number): string {
    const empleado = this.empleados.find((e) => e.uid === idEmpleado);
    return empleado ? empleado.name : '';
  }

  /**
   * Obtiene el nombre del estado del registro de horas.
   * @param estado El estado del registro de horas.
   * @returns El nombre del estado del registro de horas.
   */
  nombreEstado(estado: number): string {
    return EstadoRegistro[estado] || 'Desconocido';
  }

  obtenerRegistrosPorEmpleado(): void {
    if (!Utils.IsTierFour()) {
      this.snackBar.open(consts.UPDATE_SUBSCRIPTION_TIER_MESSAGE, null, {
        duration: 3000,
      });
      return;
    }

    if (this.formBusqueda.valid) {
      this.cargarRegistros();
    }
  }

  setRow(row: RegistroHoras): void {
    this.row = row;
  }

  detenerRegistro(): void {
    // Implementar la lógica para detener el registro actual
  }

  // #region Vista agregada de empresa

  toggleVistaEmpresa(): void {
    if (!Utils.IsTierFour()) {
      this.snackBar.open(consts.UPDATE_SUBSCRIPTION_TIER_MESSAGE, null, {
        duration: 3000,
      });
      return;
    }

    this.mostrarEmpresa = !this.mostrarEmpresa;
    if (!this.mostrarEmpresa) {
      this.registrosEmpresa = [];
      this.registrosEmpresaCargados = false;
    }
  }

  buscarRegistrosEmpresa(): void {
    if (!this.formRangoEmpresa.valid) {
      return;
    }

    const fechaInicio = moment(this.formRangoEmpresa.get('fechaInicio').value).startOf('day');
    const fechaFin = moment(this.formRangoEmpresa.get('fechaFin').value).endOf('day');

    const filtro: RegistroHorasFilter = {
      fechaInicio: fechaInicio.toDate(),
      fechaFin: fechaFin.toDate(),
      pageSize: TiempoComponent.TAMANO_PAGINA_EMPRESA,
    };

    this.registroHorasService.getRegistrosHorasEmpresa(filtro).subscribe({
      next: (response: PaginatedResult<RegistroDetalleTiempo>) => {
        this.registrosEmpresa = (response && response.items) || [];
        this.registrosEmpresaCargados = true;

        if (this.registrosEmpresa.length === 0) {
          this.snackBar.open('No se encontraron registros para el rango de fechas seleccionado.', 'Cerrar', {
            duration: 6000,
            panelClass: ['mat-toolbar', 'mat-warn'],
          });
        } else if (response.totalItems > this.registrosEmpresa.length) {
          this.snackBar.open(
            `Mostrando ${this.registrosEmpresa.length} de ${response.totalItems} registros. Acota el rango de fechas para ver el resto.`,
            'Cerrar',
            { duration: 8000 }
          );
        }
      },
      error: () => {
        this.snackBar.open('Error al cargar los registros de la empresa', 'Cerrar', {
          duration: 3000,
        });
      },
    });
  }

  private rangoEmpresaActual(): RangoFechasRegistro_DTO {
    return {
      inicio: moment(this.formRangoEmpresa.get('fechaInicio').value).format('DD/MM/YYYY'),
      fin: moment(this.formRangoEmpresa.get('fechaFin').value).format('DD/MM/YYYY'),
    };
  }

  exportarEmpresaExcel(): void {
    if (!this.formRangoEmpresa.valid) return;

    this.registroHorasService.informeHorasExcelEmpresa(this.rangoEmpresaActual()).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      },
    });
  }

  exportarEmpresaPdf(): void {
    if (!this.formRangoEmpresa.valid) return;

    this.registroHorasService.informeHorasPdfEmpresa(this.rangoEmpresaActual()).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      },
    });
  }

  exportarEmpresaCsv(): void {
    if (!this.formRangoEmpresa.valid) return;

    this.registroHorasService.informeHorasCsvEmpresa(this.rangoEmpresaActual()).subscribe({
      next: (blob: Blob) => {
        FileSaver.saveAs(blob, 'InformeHorasEmpresa.csv');
      },
    });
  }

  validarRegistroEmpresa(registro: RegistroDetalleTiempo): void {
    this.registroHorasService.validarRegistro(registro).subscribe({
      next: (resultado) => this.reemplazarRegistroEmpresa(resultado),
    });
  }

  rechazarRegistroEmpresa(registro: RegistroDetalleTiempo): void {
    const dialogRef = this.dialog.open(MotivoDialogComponent, {
      width: '480px',
      maxWidth: '96vw',
      data: { titulo: 'Rechazar registro', motivoLabel: 'Motivo del rechazo' },
    });

    dialogRef.afterClosed().subscribe((motivo: string) => {
      if (!motivo) return;

      this.registroHorasService
        .rechazarRegistro(registro.registroHorasId, { motivo })
        .subscribe({ next: (resultado) => this.reemplazarRegistroEmpresa(resultado) });
    });
  }

  editarRegistroEmpresa(registro: RegistroDetalleTiempo): void {
    const dialogRef = this.dialog.open(EditarRegistroModalComponent, {
      width: '480px',
      maxWidth: '96vw',
      data: { registro },
    });

    dialogRef.afterClosed().subscribe((resultado: RegistroDetalleTiempo) => {
      if (!resultado) return;
      this.reemplazarRegistroEmpresa(resultado);
    });
  }

  verHistorialEmpresa(registro: RegistroDetalleTiempo): void {
    this.dialog.open(HistorialRegistroModalComponent, {
      width: '560px',
      maxWidth: '96vw',
      maxHeight: '80vh',
      data: { registroHorasId: registro.registroHorasId },
    });
  }

  private reemplazarRegistroEmpresa(resultado: RegistroDetalleTiempo): void {
    const index = this.registrosEmpresa.findIndex(
      (r) => r.registroHorasId === resultado.registroHorasId
    );
    if (index !== -1) {
      this.registrosEmpresa[index] = resultado;
    }
  }

  // #endregion

  // #endregion

  // #region Métodos privados

  private inicializarFormulario(): void {
    this.formBusqueda = new UntypedFormGroup({
      empleado: new UntypedFormControl(null, [Validators.required])
    });
  }

  // #endregion
  public trackByIndex(i: number) { return i; }

} 